'use client';
/**
 * PANNEAU DE FICHE LIÉE — ouvrir sans quitter (12/08/2026).
 *
 * ── Le problème qu'il règle ─────────────────────────────────────────────────
 *
 * Le recensement du 12/08 a compté 53 sauts d'un objet à l'autre dans le
 * module ; 9 seulement gardaient un chemin de retour. Cliquer la NC citée par
 * un équipement, l'action qui bloque une clôture ou le document visé par une
 * demande faisait perdre l'écran de départ : ses filtres, son onglet, sa
 * position de lecture, et le travail en cours de saisie.
 *
 * ── Pourquoi un panneau, et pas une navigation ──────────────────────────────
 *
 * Le panneau se SUPERPOSE : l'écran de départ reste monté dessous, intact. Le
 * fermer rend exactement l'état d'avant — il n'y a rien à restaurer, parce
 * qu'on n'est jamais parti. C'est ce que faisait déjà `FenetreAction`
 * au-dessus d'une non-conformité, et dont ce composant généralise le principe.
 *
 * ── Ce qu'il n'est pas ──────────────────────────────────────────────────────
 *
 * Il ne remplace pas les écrans de plein droit. Pour le travail de fond — une
 * analyse de cause, une fiche de vie complète — l'en-tête porte « Ouvrir en
 * pleine page », qui bascule sur la vue entière EN ALIMENTANT le fil d'Ariane.
 * Le panneau est un aller simple vers ce qu'on veut consulter, pas un second
 * jeu d'écrans à maintenir.
 *
 * ── Empilement ──────────────────────────────────────────────────────────────
 *
 * Un lien cliqué DANS un panneau ouvre le suivant par-dessus (quatre niveaux
 * au plus, cf. `ouvrirPanneau`). L'en-tête montre alors le chemin et « Retour »
 * redonne le panneau précédent. Échap et le clic sur le fond ferment TOUT :
 * arrivé à trois niveaux de profondeur, ce qu'on veut est presque toujours
 * revenir à son écran, pas remonter marche par marche.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import { droitsNav } from '@/lib/navigation';
import { VOILE, I, EmptyState, useEscape, COUCHES } from '@/components/ui';
import { ActionDossier } from '@/components/screens/Actions';
import { NCDossier, ncSoumise, peutOuvrirNCNonRetenue } from '@/components/screens/NC';
import { AuditDossier } from '@/components/screens/Audits';
import { ReclamationDossier } from '@/components/screens/Reclamations';
import { EquipementDossier } from '@/components/screens/ressources/EquipFiche';
import { DocDossier } from '@/components/screens/DocDetail';
import { DossierParametreLie } from '@/components/screens/manuel/ReferentielParametres';

import { Bouton } from '@/shared/Bouton';
/** Ce que l'en-tête annonce, par nature d'objet. */
const NATURES: Record<string, { nom: string; feminin: boolean }> = {
  nc: { nom: 'non-conformité', feminin: true },
  action: { nom: 'action corrective', feminin: true },
  doc: { nom: 'document', feminin: false },
  equip: { nom: 'équipement', feminin: false },
  recl: { nom: 'réclamation', feminin: true },
  audit: { nom: 'audit', feminin: false },
  maintenance: { nom: 'plan de maintenance', feminin: false },
  parametre: { nom: 'paramètre', feminin: false },
};

/**
 * LA SECTION DONT DÉPEND LA LECTURE DE CHAQUE NATURE (05/10/2026).
 *
 * ── Le défaut refermé ──────────────────────────────────────────────────────
 *
 * Signalé sur un compte `technician` (`sec.equip.voir = false`, mais
 * `sec.maintenances.voir = true`) : ouvrir un Indiko depuis une maintenance du
 * calendrier répondait « Cet équipement n'est plus au parc. »
 *
 * L'appareil Y ÉTAIT. La garde de section du moteur
 * (`middlewares/garde-section.ts`) refusait `GET /ged-equipements`, le magasin
 * récupère les collections sans jamais signaler un refus (`getAll` avale
 * l'erreur), si bien que `equips` était VIDE — et une recherche dans une liste
 * vide ne trouve rien. Le panneau en concluait une sortie du parc.
 *
 * ⚠️ C'est le piège des faux vides, dans sa forme la plus coûteuse : l'écran
 * AFFIRMAIT un fait sur le laboratoire (« cet appareil a été sorti du
 * service ») là où la vérité portait sur le lecteur (« cette rubrique ne vous
 * est pas ouverte »). Les deux phrases appellent des gestes opposés : la
 * première envoie chercher un appareil disparu, la seconde demande un droit.
 *
 * On ne devine donc plus : si la section est fermée, on le DIT, et aucune
 * absence de données n'est interprétée.
 */
const SECTION_DE: Record<string, string> = {
  nc: 'nc',
  action: 'actions',
  doc: 'list',
  equip: 'equip',
  recl: 'recl',
  audit: 'audits',
  maintenance: 'maintenances',
  parametre: 'manuel',
};

/** Les natures qui ont une vue de plein droit — celles que `panneauEnPleinePage`
 *  sait ouvrir. Le paramètre n'en a pas : sa fiche complète est ce panneau. */
const PLEINE_PAGE = new Set(['nc', 'action', 'doc', 'equip', 'recl', 'audit', 'maintenance']);

export function PanneauFiche() {
  const { pilePanneaux, fermerPanneau, fermerTousPanneaux, panneauEnPleinePage, actions, ncs, audits, recls, equips, session, rhAcces } = useGed();
  const courant = pilePanneaux[pilePanneaux.length - 1] || null;
  // ⚠️ `useEscape` AVANT tout retour anticipé : le nombre de hooks ne doit pas
  // varier entre deux rendus.
  useEscape(!!courant, fermerTousPanneaux);
  if (!courant) return null;

  const nature = NATURES[courant.type] || { nom: 'fiche', feminin: true };
  const profondeur = pilePanneaux.length;
  /* ⚠️ Le droit se lit AVANT d'interpréter une absence — voir `SECTION_DE`.
     Même source que la barre latérale : le moteur a calculé, l'écran ne
     recalcule pas. */
  const sectionRequise = SECTION_DE[courant.type];
  const sectionFermee = !!sectionRequise
    && !droitsNav(session, !!rhAcces?.acces).section(sectionRequise);

  /** Le contenu, par nature. Chaque fiche est rendue par le composant de son
   *  écran — jamais une copie : deux rendus du même objet finiraient par dire
   *  deux choses différentes. */
  const contenu = (() => {
    /* Le refus se dit AVANT la recherche dans le magasin : sans cela, une
       collection refusée par le moteur se lirait comme une fiche disparue. */
    if (sectionFermee) {
      return (
        <EmptyState
          titre={`La fiche ${nature.feminin ? 'd’une' : 'd’un'} ${nature.nom} ne vous est pas ouverte.`}
          indice={'Cette fiche existe — c’est sa rubrique qui n’est pas ouverte par vos rôles. '
            + 'Les rôles et ce qu’ils ouvrent se règlent dans Paramétrage → Rôles & accès.'} />
      );
    }
    switch (courant.type) {
      case 'action': {
        const a = actions.find((x) => x.documentId === courant.valeur);
        return a
          ? <ActionDossier key={a.documentId} a={a} enPanneau />
          : <EmptyState titre="Cette action n’est plus au plan d’actions." indice="Elle a pu être annulée depuis l’ouverture de cet écran." />;
      }
      case 'nc': {
        const n = ncs.find((x) => x.documentId === courant.valeur);
        /* ⚠️ LE PANNEAU EST UNE SECONDE PORTE VERS LE MÊME DOSSIER, et
           `NCDossier` n'est pas un aperçu : il porte les formulaires. Le verrou
           du triage (07/10/2026) devait donc être posé ICI AUSSI — le fermer au
           registre et au lien profond en le laissant ouvert au panneau des
           fiches liées n'aurait rien fermé du tout. C'est le défaut
           caractérisé le 30/08 sur l'écran Analytes : une porte d'écriture
           qu'on avait oublié de compter. */
        if (n && ncSoumise(n) && !peutOuvrirNCNonRetenue(session.roles)) {
          return (
            <EmptyState
              titre="Cette déclaration n’est pas encore retenue comme non-conformité."
              indice={'Son dossier ne s’ouvre qu’au responsable qualité et au coordinateur qualité, qui '
                + 'décident d’abord si c’en est une. Vous serez informé de la décision.'} />
          );
        }
        return n
          ? <NCDossier key={n.documentId} n={n} enPanneau />
          : <EmptyState titre="Cette non-conformité n’est plus au registre." indice="Elle a pu être annulée depuis l’ouverture de cet écran." />;
      }
      case 'audit': {
        const a = audits.find((x) => x.documentId === courant.valeur);
        return a
          ? <AuditDossier key={a.documentId} a={a} enPanneau />
          : <EmptyState titre="Cet audit n’est plus au programme." indice="Il a pu être annulé depuis l’ouverture de cet écran." />;
      }
      case 'doc':
        // Pour un document, `valeur` porte le CODE et non le documentId : c'est
        // la clé que le catalogue, les demandes et la diffusion manipulent.
        /* `onglet` / `voirEnCours` : une TÂCHE ouvre la fiche sur ce qu'il y a à
           faire — et sur la version en chantier, pas sur l'applicable (RQ,
           06/10/2026). Transmis en props, jamais par le magasin : l'onglet du
           panneau est LOCAL (voir `Fiche`). */
        return <DocDossier key={courant.valeur} code={courant.valeur} enPanneau
          onglet={courant.onglet} voirEnCours={courant.voirEnCours} />;
      case 'recl': {
        const r = recls.find((x) => x.documentId === courant.valeur);
        return r
          ? <ReclamationDossier key={r.documentId} r={r} enPanneau />
          : <EmptyState titre="Cette réclamation n’est plus au registre." indice="Elle a pu être retirée depuis l’ouverture de cet écran." />;
      }
      case 'equip': {
        const e = equips.find((x) => x.documentId === courant.valeur || x.code === courant.valeur);
        // ⚠️ Le panneau ne porte que l'identification, la fiche de vie et les
        // pièces : la mise en service, les maintenances, les calibrations et le
        // journal vivent hors du fichier de fiche. « Ouvrir en pleine page »
        // reste le chemin pour eux, et l'encart ci-dessous le DIT — un panneau
        // qui tait ce qu'il ne montre pas ferait conclure à un équipement sans
        // maintenance.
        return e
          ? (<>
              <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 10px' }}>
                Aperçu : identification, métrologie, fiche de vie et pièces. Les maintenances,
                calibrations, contrôles internes et le journal s’ouvrent en pleine page.
              </p>
              <EquipementDossier key={e.documentId} e={e} enPanneau />
            </>)
          : <EmptyState titre="Cet équipement n’est plus au parc." indice="Il a pu être sorti du service depuis l’ouverture de cet écran." />;
      }
      case 'parametre':
        // LE dossier du paramètre — le même que dans la liste des paramètres
        // (18/10/2026 : un seul dossier par paramètre). Il se charge lui-même
        // et accepte le code comme le documentId.
        return (
          <DossierParametreLie key={courant.valeur} reference={courant.valeur}
            estRq={session.roles.includes('rq')} />
        );
      default:
        return (
          <EmptyState titre={`Aperçu de ${nature.feminin ? 'cette' : 'ce'} ${nature.nom} en préparation`}
            indice="Utilisez « Ouvrir en pleine page » — la fiche complète s’affiche, et le fil d’Ariane vous ramène ici." />
        );
    }
  })();

  return (
    <div onMouseDown={fermerTousPanneaux} role="presentation"
      style={{
        /* ⚠️ `COUCHES.PANNEAU`, et non plus 300 en dur (30/08/2026). À 300 —
           la couche des commandes FLOTTANTES — ce panneau passait SOUS toute
           fenêtre modale. Ouvert depuis le dossier d'un examen, il se montait
           derrière lui : le clic paraissait ne rien faire. */
        position: 'fixed', inset: 0, zIndex: COUCHES.PANNEAU, background: VOILE,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}>
      <div onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true"
        aria-label={`${nature.nom} ouverte depuis ${courant.origine}`}
        style={{
          width: '100%', maxWidth: 940, maxHeight: '88vh', background: 'var(--surface-raised, #fff)',
          borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 20px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-sunken)' }}>
          {profondeur > 1 && (
            <Bouton taille="sm" onClick={fermerPanneau} title="Revenir à la fiche précédente">
              <I n="chevron-left" s={13} />Retour
            </Bouton>
          )}
          {/* On DIT ce qui est resté ouvert dessous : sans cette ligne, la
              superposition se lit comme une navigation de plus. */}
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            {nature.feminin ? 'Ouverte' : 'Ouvert'} depuis{' '}
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--color-primary-active)' }}>{courant.origine}</span>,
            qui reste ouvert dessous.
          </span>
          <span style={{ flex: 1 }} />
          {/* Pas de bouton là où aucune vue de plein droit n'existe : un
              bouton inerte se lit comme une panne. Le paramètre est dans ce
              cas — sa fiche complète VIT dans ce panneau. */}
          {PLEINE_PAGE.has(courant.type) && (
          <Bouton taille="sm" onClick={panneauEnPleinePage} title="Afficher la fiche entière — le fil d’Ariane vous proposera de revenir ici">
            Ouvrir en pleine page →
          </Bouton>
          )}
          <Bouton taille="sm" onClick={fermerTousPanneaux} aria-label="Fermer et revenir à l’écran"><I n="x" s={14} /></Bouton>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px 20px' }}>{contenu}</div>
      </div>
    </div>
  );
}
